import { ATTRIBUTION_KEYS, UTM_PARAMS } from 'constants/attribution';
import type { AttributionHistoryEntry, UTM } from 'stores/zustand/attributionStore';
import { getLocalStorageWithExpiration } from 'utils/storage';

const ATTRIBUTION_HISTORY_KEY = 'kalshi_attribution_history';
const MAX_HISTORY_ENTRIES = 50;
const ATTRIBUTION_TOUCHPOINT_POST_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const PRO_REDIRECT_ORIGIN = 'https://pro.kalshi.com';

export const getAttributionSearchParams = (searchParams: URLSearchParams): URLSearchParams => {
    const resolved = new URLSearchParams(searchParams);
    if (resolved.get('embed') !== 'pro') return resolved;

    const proRedirect = resolved.get('pro_redirect')?.trim();
    if (!proRedirect?.startsWith('/') || proRedirect.startsWith('//') || proRedirect.includes('\\')) {
        return resolved;
    }

    try {
        const redirectUrl = new URL(proRedirect, PRO_REDIRECT_ORIGIN);
        if (redirectUrl.origin !== PRO_REDIRECT_ORIGIN) return resolved;

        for (const key of UTM_PARAMS) {
            const value = redirectUrl.searchParams.get(key);
            if (!resolved.has(key) && value !== null) resolved.set(key, value);
        }
    } catch {
        return resolved;
    }

    return resolved;
};

export const isWithinAttributionTouchpointPostWindow = (createdTs: string | undefined): boolean => {
    if (!createdTs) return false;

    const createdAtMs = new Date(createdTs).getTime();
    if (!Number.isFinite(createdAtMs)) return false;

    return Date.now() - createdAtMs <= ATTRIBUTION_TOUCHPOINT_POST_WINDOW_MS;
};

/**
 * Resolve utm_* values for a backend submission (signup, oauth, etc.) using the
 * freshest synchronously-available sources.
 *
 * `useRouteLogic` is mounted inside `<SearchParamsBoundary>`, which is loaded
 * asynchronously via next/dynamic. On a fast direct-deeplink signup
 * (e.g. `/sign-up?utm_source=metaweb_int` followed by an immediate submit) the
 * in-memory `utmData` may still be empty when the signup payload is built. Mirror
 * the priority used by `useRouteLogic`'s computed chain so callers get the
 * same answer regardless of whether the route effect has fired yet.
 *
 * Order: in-memory utmData → URL query string → localStorage (90-day persisted).
 */
export const resolveUtmDataForSubmission = (utmData: UTM): UTM => {
    if (typeof window === 'undefined') return utmData;

    let urlParams: URLSearchParams | null = null;
    try {
        urlParams = getAttributionSearchParams(new URLSearchParams(window.location.search));
    } catch {
        urlParams = null;
    }

    const fromUrl = (key: string): string | undefined => urlParams?.get(key) ?? undefined;
    const fromStorage = (key: keyof typeof ATTRIBUTION_KEYS): string | undefined =>
        getLocalStorageWithExpiration(ATTRIBUTION_KEYS[key]) ?? undefined;

    const resolved: UTM = { ...utmData };
    const storageKey: Record<(typeof UTM_PARAMS)[number], keyof typeof ATTRIBUTION_KEYS> = {
        utm_source: 'UTM_SOURCE',
        utm_medium: 'UTM_MEDIUM',
        utm_campaign: 'UTM_CAMPAIGN',
        utm_content: 'UTM_CONTENT',
        utm_term: 'UTM_TERM',
    };

    for (const key of UTM_PARAMS) {
        if (resolved[key] != null && resolved[key] !== '') continue;
        resolved[key] = fromUrl(key) ?? fromStorage(storageKey[key]);
    }

    return resolved;
};

export const getAttributionHistory = (): AttributionHistoryEntry[] => {
    if (
        typeof window === 'undefined' ||
        !window.localStorage ||
        typeof window.localStorage.getItem !== 'function' ||
        typeof window.localStorage.setItem !== 'function' ||
        typeof window.localStorage.removeItem !== 'function'
    ) {
        return [];
    }

    try {
        const stored = window.localStorage.getItem(ATTRIBUTION_HISTORY_KEY);
        if (!stored) return [];

        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        console.error('Failed to parse attribution history from localStorage:', e);
        return [];
    }
};

// Slice sizes to retry with when localStorage rejects a write (quota exceeded).
const HISTORY_SIZE_FALLBACKS = [MAX_HISTORY_ENTRIES, 10, 1];

export const saveAttributionHistory = (history: AttributionHistoryEntry[]): void => {
    if (typeof window === 'undefined' || !window.localStorage || typeof window.localStorage.setItem !== 'function') {
        return;
    }

    for (const size of HISTORY_SIZE_FALLBACKS) {
        try {
            window.localStorage.setItem(ATTRIBUTION_HISTORY_KEY, JSON.stringify(history.slice(-size)));
            return;
        } catch {
            // localStorage is full (possibly from unrelated keys) — retry with a smaller slice.
        }
    }

    // Even a single entry won't fit — drop the stored history so future writes have room.
    try {
        window.localStorage.removeItem(ATTRIBUTION_HISTORY_KEY);
    } catch {
        // Nothing left to try; stay silent so this doesn't surface as a RUM error.
    }
    console.warn('Failed to save attribution history to localStorage; cleared stored history.');
};

export const clearAttributionHistory = (): void => {
    if (typeof window === 'undefined' || !window.localStorage || typeof window.localStorage.removeItem !== 'function') {
        return;
    }

    window.localStorage.removeItem(ATTRIBUTION_HISTORY_KEY);
};

export const addAttributionEntry = (entry: AttributionHistoryEntry): boolean => {
    const history = getAttributionHistory();

    // Check if last entry is identical (ignoring timestamp and event_id) to avoid duplicates.
    // event_id is excluded because it's always a unique UUID and shouldn't affect deduplication.
    // anonymous_id, cid, and initial_referrer are per-user context rather than per-touchpoint
    // identity, so they are also excluded from the comparison.
    // Click IDs (gclid, fbclid, msclkid, dclid, rdt_cid, ttclid, twclid, sc_cid) ARE included: two arrivals with the same UTM
    // params but different click IDs represent distinct ad touchpoints and must not be collapsed.
    const lastEntry = history[history.length - 1];
    if (lastEntry) {
        const isSame =
            lastEntry.utm_source === entry.utm_source &&
            lastEntry.utm_medium === entry.utm_medium &&
            lastEntry.utm_campaign === entry.utm_campaign &&
            lastEntry.utm_term === entry.utm_term &&
            lastEntry.utm_content === entry.utm_content &&
            lastEntry.utm_landing === entry.utm_landing &&
            lastEntry.gclid === entry.gclid &&
            lastEntry.fbclid === entry.fbclid &&
            lastEntry.msclkid === entry.msclkid &&
            lastEntry.dclid === entry.dclid &&
            lastEntry.rdt_cid === entry.rdt_cid &&
            lastEntry.ttclid === entry.ttclid &&
            lastEntry.twclid === entry.twclid &&
            lastEntry.sc_cid === entry.sc_cid;

        if (isSame) return false;
    }

    history.push(entry);
    saveAttributionHistory(history);
    return true;
};
